import React, { useLayoutEffect, useRef } from "react";
import { getImage } from "gatsby-plugin-image";
import { BgImage } from "gbimage-bridge";
import { Col, Container, Row } from "react-bootstrap";
import { gsap } from "gsap";
import { SafeHtmlParser } from "./SafeHtmlParser"; // adjust import path if needed

const Header2 = ({ heading, body, backgroundImage }) => {
	const bgImage = getImage(backgroundImage);
	const headingRef = useRef(null);
	const bodyRef = useRef(null);

	useLayoutEffect(() => {
		// Helper function that splits an element's text into word-wrappers
		// and then animates each inner span upward.
		const animateSplitText = (element) => {
			if (!element) return;

			// Hide the element initially.
			element.style.visibility = "hidden";
			element.style.opacity = "0";

			// Save the original text and clear the element.
			const originalText = element.textContent;
			element.textContent = "";

			// Build word wrappers.
			const wordWrappers = originalText.split(" ").map((word) => {
				const wrapper = document.createElement("div");
				wrapper.style.overflow = "hidden";
				wrapper.style.display = "inline-block";
				wrapper.style.marginRight = "0.25rem";

				const span = document.createElement("span");
				span.textContent = word;
				span.style.display = "inline-block";
				// Start each word shifted downward.
				span.style.transform = "translateY(100%)";

				wrapper.appendChild(span);
				return wrapper;
			});

			// Append the word wrappers to the element.
			wordWrappers.forEach((wrapper) => element.appendChild(wrapper));

			// Force a reflow (optional) and wait a frame for layout.
			void element.offsetHeight;
			requestAnimationFrame(() => {
				// Set a fixed height based on the final layout.
				const finalHeight = element.getBoundingClientRect().height;
				element.style.height = `${finalHeight}px`;

				// Reveal the element.
				element.style.visibility = "visible";
				element.style.opacity = "1";

				// Select all inner spans.
				const spans = element.querySelectorAll("span");

				// Set their initial state and animate them upward.
				gsap.set(spans, { y: "100%" });
				gsap.to(spans, {
					y: "0%",
					duration: 0.6,
					stagger: 0.08,
					delay: 0.2,
					ease: "power3.out",
				});
			});
		};
		const animateSplitHeading = (element) => {
			if (!element) return;

			// Hide the element initially.
			element.style.visibility = "hidden";
			element.style.opacity = "0";

			// Save the original text and clear the element.
			const originalText = element.textContent;
			element.textContent = "";

			// Build word wrappers.
			const wordWrappers = originalText.split(" ").map((word) => {
				const wrapper = document.createElement("div");
				wrapper.style.overflow = "hidden";
				wrapper.style.display = "inline-block";
				wrapper.style.marginRight = "0.6rem";

				const span = document.createElement("span");
				span.textContent = word;
				span.style.display = "inline-block";
				// Start each word shifted downward.
				span.style.transform = "translateY(100%)";

				wrapper.appendChild(span);
				return wrapper;
			});

			// Append the word wrappers to the element.
			wordWrappers.forEach((wrapper) => element.appendChild(wrapper));

			// Force a reflow (optional) and wait a frame for layout.
			void element.offsetHeight;
			requestAnimationFrame(() => {
				// Set a fixed height based on the final layout.
				const finalHeight = element.getBoundingClientRect().height;
				element.style.height = `${finalHeight}px`;

				// Reveal the element.
				element.style.visibility = "visible";
				element.style.opacity = "1";

				// Select all inner spans.
				const spans = element.querySelectorAll("span");

				// Set their initial state and animate them upward.
				gsap.set(spans, { y: "100%" });
				gsap.to(spans, {
					y: "0%",
					duration: 0.6,
					stagger: 0.08,
					delay: 0.2,
					ease: "power3.out",
				});
			});
		};

		// Animate both the heading and body elements.
		animateSplitHeading(headingRef.current);
		animateSplitText(bodyRef.current);
	}, [heading, body]);

	return (
		<BgImage image={bgImage} className="position-relative py-7 py-lg-9">
			<div
				className="h-100 w-100 position-absolute start-0 top-0"
				style={overlayStyles}
			/>
			<Container style={{ zIndex: 2 }} className="h-100 position-relative">
				<Row className="justify-content-center align-items-end align-items-md-center h-100 text-center">
					<Col className="text-white" lg={8}>
						<h1 className="display-3" ref={headingRef}>
							{heading}
						</h1>
						<div className="pt-3" ref={bodyRef}>
							<SafeHtmlParser htmlContent={body} />
						</div>
					</Col>
				</Row>
			</Container>
		</BgImage>
	);
};

const overlayStyles = {
	background:
		"linear-gradient(180deg, rgba(0, 18, 33, 0.00) 6.66%, #001221 95.38%)",
};

export default Header2;
